import { Helmet } from "react-helmet-async";
import "./styles/Body.css";
import ContactForm from "./ContactForm";

const SITE_URL = "https://elamuaudit.ee";
const PAGE_URL = `${SITE_URL}/energiamargis`;

const TITLE = "Energiamärgis – KEK energiamärgise koostamine | Elamuaudit OÜ";
const DESCRIPTION =
  "Elamuaudit OÜ pakub olemasolevatele hoonetele KEK energiamärgise koostamise teenust. Tutvu tingimustega ja esita hinnapäring.";

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Energiamärgis",
  name: "KEK energiamärgise koostamine",
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: { "@type": "Country", name: "Estonia" },
  url: PAGE_URL,
  description:
    "KEK energiamärgise koostamise teenus olemasolevatele hoonetele. Energiamärgise määramiseks on vaja viimase kolme aasta tarbimisandmeid.",
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Mis on energiamärgis?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Energiamärgis annab ülevaate hoone energiakasutusest ja -tõhususest. Olemasolevate hoonete müümise või välja üürimise puhul on energiamärgis nõutav.",
      },
    },
    {
      "@type": "Question",
      name: "Mille poolest erinevad ETA ja KEK energiamärgis?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "ETA on arvutuslik energiatõhususarv projekteeritavatele või oluliselt rekonstrueeritavatele hoonetele ja kehtib 2 aastat alates kasutusloa väljastamisest. KEK on kaalutud energiakasutus olemasolevatele hoonetele, mis tugineb tegelikule mõõdetud energiabilansile ja kehtib 10 aastat.",
      },
    },
    {
      "@type": "Question",
      name: "Mis on hoone energiatõhususe klass?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Energiamärgise arvutamise tulemusel määratakse hoone energiatõhususe klass vahemikus A–H, kus A on kõige energiatõhusam ja H ebatõhusam klass. Energiamärgis kantakse seejärel ehitisregistrisse.",
      },
    },
    {
      "@type": "Question",
      name: "Mida on vaja KEK energiamärgise koostamiseks?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Energiamärgise määramiseks on vaja viimase kolme aasta tarbimisandmeid. Minimaalseks tarbimisperioodiks on üks täisaasta.",
      },
    },
  ],
};

function Energiamargis() {
  return (
    <main className="site-body">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />

        <meta property="og:type" content="article" />
        <meta property="og:locale" content="et_EE" />
        <meta property="og:site_name" content="Elamuaudit OÜ" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1954" />
        <meta property="og:image:height" content="1026" />
        <meta property="og:image:alt" content="Elamuaudit OÜ – energiamärgis ja energiaaudit" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <section className="container content-section energiamargis-page">
        <h1>Energiamärgis</h1>

        <p>
          Energiamärgis annab ülevaate hoone energiakasutusest ja -tõhususest. Olemasolevate
          hoonete müümise või välja üürimise puhul on energiamärgis nõutav. Lisaks võimaldab
          energiamärgis hoone omanikul hinnata renoveerimise või küttesüsteemide asendamise
          otstarbekust. Projekteeritavate või oluliselt rekonstrueeritavate hoonete puhul on
          energiamärgis nõutud, et tõendada hoone vastavust energiatõhususe miinimumnõuetele.
        </p>

        <p>
          Projekteeritavate või oluliselt rekonstrueeritavate hoonete puhul määratakse
          energiatõhusus energiatõhususarvu ehk ETA abil. ETA on arvutuslik summaarne tarnitud
          energiate kaalutud erikasutus hoone tüüpilisel kasutusel hoone köetava pinna ruutmeetri
          kohta. ETA määratakse ehitusprojekti andmete ja hoone kasutusandmete põhjal. ETA
          energiamärgis kehtib 2 aastat alates hoone kasutusloa väljastamisest.
        </p>

        <p>
          Olemasolevate hoonete puhul hinnatakse energiatõhusust kaalutud energiakasutuse ehk KEK
          abil. KEK tugineb hoone tegelikule ja mõõdetud energiabilansile ning see leitakse
          tarnitud ja eksporditud energiamahtude järgi. KEK energiamärgis kehtib 10 aastat.
        </p>

        <p>
          Energiamärgise arvutamise tulemusel määratakse hoone energiatõhususe klass, mis jääb
          vahemikku A–H, kus A on kõige energiatõhusam ja H ebatõhusam klass. Energiamärgis
          kantakse seejärel ehitisregistrisse.
        </p>

        <p>
          Meie pakume olemasolevatele hoonetele KEK energiamärgise koostamise teenust.
          Energiamärgise määramiseks on vaja viimase kolme aasta tarbimisandmeid, minimaalseks
          tarbimisperioodiks on üks täisaasta.
        </p>

        <p>Esita hinnapäring all toodud vormis.</p>
      </section>

      <section className="cta-section" id="contact">
        <div className="cta-inner">
          <h2>Hinnapäring</h2>
          <div className="cta-form-wrapper">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}

export default Energiamargis;
